import React from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import { Alert, IconButton } from '@mui/material';
import { Command, tabActions } from '@store/slices';
import { useDispatch, useSelector } from 'react-redux';
import { AddOutlined, DeleteOutlineOutlined } from '@mui/icons-material';
import { Button } from '@common/Button';
import { Label } from '@common/Label';
import { useTheme } from '../../../../styles/theme';
import { CallHeader, NodeField, NodeInputType } from '@components/Flow/Nodes';
import { Expression } from '@common/Expression';
import { useNodeData } from '@common/hooks/useNodeData';
import { BasicInput } from '@common/Expression/Basic';
import { ErrorMessage } from '@common/Autocomplete';
import { State } from '@store/index';
import { useParams } from 'react-router-dom';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 8px;

  & .MuiInputAdornment-root {
    margin-right: 10px;
  }

  &:hover {
    .switch {
      display: block;
    }
  }
`;

const ParameterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 auto;
  width: 100%;
  max-width: calc((100vw - 60px) * (2.5 / (7.5 + 2.5)));
`;

const Parameter = styled.div`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 3fr 7fr 42px;
`;

const Action = styled.div`
  display: flex;
  align-items: center;

  svg {
    font-size: 2ch;
  }
`;

interface Props {
  command: Command;
  field: NodeField;
  headerType: 'sip' | 'custom';
}

export const CallHeaders: React.FC<Props> = ({ command, field, headerType }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { state } = command;
  const { id, label, description } = field;
  const { getOptions, dataEdges } = useNodeData(command?.node?.id);
  const { workflow_id } = useParams();

  const nodeErrors = useSelector((state: State) => state.tabs.active.nodeErrors);
  const dataOptions = getOptions(command?.node?.id);

  const headers = state[id]?.value as CallHeader[];
  const type = state[id]?.type;

  const addParameter = () => {
    const value = { id: uuidv4(), name: '', value: '' };

    const input = {
      nodeId: command.node.id,
      fieldId: id,
      value,
      nestedFieldName: 'value',
    };

    dispatch(tabActions.nodeInputNestedArrayPush(input));
    dispatch(tabActions.setSaved(false));
  };

  const convertName = (input: string) => {
    let formatted = input.replace(/ /g, '_');
    formatted = formatted.replace(/[^a-zA-Z0-9_\-]/g, '');
    return formatted.toLowerCase();
  };

  const updateField = (nestedFieldId: string, fieldName: 'value' | 'name' | undefined, newValue: string) => {
    if (fieldName === 'name') {
      newValue = convertName(newValue);
    }

    if (type === NodeInputType.CallHeader) {
      const input = {
        nodeId: command.node.id,
        fieldId: id,
        nestedFieldId,
        fieldName,
        nestedFieldName: 'value',
        value: newValue,
      };

      dispatch(tabActions.nodeInputNestedArrayUpdate(input));
    } else {
      const valueUpdate = {
        nodeId: command.node.id,
        fieldId: id,
        value: newValue,
        fieldName,
      };

      dispatch(tabActions.nodeInputUpdate(valueUpdate));
    }

    dispatch(tabActions.setSaved(false));
  };

  const updateType = (type: string) => {
    const valueUpdate = {
      nodeId: command.node.id,
      fieldId: id,
      value: type === NodeInputType.CallHeader ? [] : '',
      fieldName: 'value',
    };

    dispatch(tabActions.nodeInputUpdate(valueUpdate));

    const typeUpdate = {
      nodeId: command.node.id,
      fieldId: id,
      value: type,
      fieldName: 'type',
    };

    dispatch(tabActions.nodeInputUpdate(typeUpdate));
    dispatch(tabActions.setSaved(false));
  };

  const deleteField = (nestedFieldId: string) => {
    dispatch(
      tabActions.nodeInputNestedArrayFilter({
        nodeId: command.node.id,
        fieldId: id,
        nestedFieldId,
        nestedFieldName: 'value',
      })
    );

    dispatch(tabActions.setSaved(false));
  };

  const totalAvailableWidth = (window.innerWidth - 60) * (2.5 / (7.5 + 2.5)) - 90;
  const basicInputMaxWidth = totalAvailableWidth * (4.2 / 10);
  const expressionMaxWidth = totalAvailableWidth * (5.8 / 10);

  const error = nodeErrors?.find(
    (nodeError) =>
      nodeError.id === command.node.id &&
      nodeError?.workflowId === workflow_id &&
      nodeError?.fields?.find((nodeField) => nodeField.id === field.id)
  );
  const errorField = error ? error?.fields?.find((nodeField) => nodeField.id === field.id) : undefined;
  const errorFieldMessage = errorField ? errorField.message : undefined;

  if (!field || !command) return null;

  const ErrorMessageNotice = errorFieldMessage ? <ErrorMessage>{errorFieldMessage}</ErrorMessage> : null;

  if (type === NodeInputType.Expression && typeof state[id]?.value === 'string') {
    return (
      <Container>
        <Label
          title={label}
          tooltip={description}
          onClick={headers?.length !== 0 && headers ? addParameter : undefined}
          alignment={type}
          customOption={{ label: 'Header', value: NodeInputType.CallHeader }}
          setAlignment={(newValue) => newValue && updateType(newValue)}
        />
        <Expression
          key={`${id}-${command.node.id}-${field.id}-value`}
          keyProp={`${id}-${command.node.id}-${field.id}`}
          value={state[id]?.value}
          dataEdges={dataEdges}
          wrapping={false}
          title={label}
          description={description}
          setValue={(newValue) => updateField(id, 'value', newValue)}
          options={dataOptions}
          placeholder='value'
          minRows={1}
          maxRows={1}
        />
        {ErrorMessageNotice}
      </Container>
    );
  }

  if (!Array.isArray(headers)) {
    return (
      <Container>
        <Label
          title={label}
          tooltip={description}
          alignment={type}
          customOption={{ label: 'Header', value: NodeInputType.CallHeader }}
          setAlignment={(newValue) => newValue && updateType(newValue)}
        />
        <Alert severity='error'>State mismatch. Please re-create this node to fix this issue.</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Label
        title={label}
        tooltip={description}
        onClick={
          headers?.length !== 0 && headers && !(headerType === 'sip' && headers.length > 0) ? addParameter : undefined
        }
        alignment={type}
        customOption={{ label: 'Header', value: NodeInputType.CallHeader }}
        setAlignment={(newValue) => newValue && updateType(newValue)}
      />
      <ParameterContainer>
        {headers?.map(({ id, name, value }) => (
          <Parameter key={`${id}-${command.node.id}-${field.id}-field`}>
            <BasicInput
              key={`${id}-${command.node.id}-${field.id}-name`}
              keyProp={`${id}-${command.node.id}-${field.id}`}
              value={headerType === 'sip' ? 'User-to-User' : name}
              setValue={(newValue) => headerType !== 'sip' && updateField(id, 'name', newValue)}
              placeholder='param'
              minRows={1}
              disabled={headerType === 'sip'}
              maxRows={1}
              wrapping={false}
              maxWidth={`${basicInputMaxWidth}px`}
            />
            <Expression
              key={`${id}-${command.node.id}-${field.id}-value`}
              keyProp={`${id}-${command.node.id}-${field.id}`}
              value={value}
              dataEdges={dataEdges}
              wrapping={false}
              title={label}
              description={description}
              setValue={(newValue) => updateField(id, 'value', newValue)}
              options={dataOptions}
              placeholder='value'
              minRows={1}
              maxRows={1}
              maxWidth={`${expressionMaxWidth}px`}
            />
            <Action>
              <IconButton
                sx={{ border: `1px solid ${theme.line.light}`, height: '42px', width: '42px' }}
                onClick={() => deleteField(id)}
              >
                <DeleteOutlineOutlined sx={{ fontSize: '1.4ch !important', color: theme.text.subtext }} />
              </IconButton>
            </Action>
          </Parameter>
        ))}
        {ErrorMessageNotice}
        {(headers?.length === 0 || !headers) && !(headerType === 'sip' && headers.length > 0) && (
          <Button color='primary' fullWidth onClick={addParameter}>
            <AddOutlined sx={{ fontSize: '14px', color: theme.text.standard }} />
          </Button>
        )}
      </ParameterContainer>
    </Container>
  );
};
